/**
 * Mirror Magic: one stroke becomes a kaleidoscope. The maths is rotational
 * symmetry — a child sees it as "my scribble turned into a snowflake".
 *
 * Marks are stored relative to the centre of the fold rather than to the
 * screen, so a pattern reopens at the right size on any device and can be
 * redrawn into a thumbnail or an export without the toy being on screen.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { FlipHorizontal2, Home, Star, Trash2, Undo2 } from 'lucide-preact';
import type { Stroke } from '../../engine/stroke.ts';
import { createStroke, extendStroke, finishStroke } from '../../engine/stroke.ts';
import { paintMirrored } from '../../engine/mirror.ts';
import { StrokeLayer } from '../../engine/layer.ts';
import { token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import type { Scene } from '../../lib/scene.ts';
import { createDraftKeeper, loadDraft } from '../../lib/drafts.ts';
import { keepPicture } from '../../lib/keep.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { Chip, Coach, ConfirmSheet, CrayonRow, Stepper, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  onSaved: () => void;
  tabs: ComponentChildren;
  initial?: Scene | null;
}

export function MirrorToy({ onExit, onSaved, tabs, initial }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layerRef = useRef(new StrokeLayer());
  const strokesRef = useRef<Stroke[]>([]);
  const activeRef = useRef(new Map<number, Stroke>());

  const [color, setColor] = useState(() => token('--crayon-purple'));
  const [folds, setFolds] = useState(6);
  const [flip, setFlip] = useState(true);
  const [count, setCount] = useState(0);
  const [askClear, setAskClear] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const live = useRef({ color, folds, flip, centre: { x: 0, y: 0 } });
  live.current.color = color;
  live.current.folds = folds;
  live.current.flip = flip;

  const sceneNow = useCallback(
    (): Scene => ({
      kind: 'mirror',
      strokes: strokesRef.current,
      folds: live.current.folds,
      flip: live.current.flip,
    }),
    []
  );
  const draftRef = useRef(createDraftKeeper('mirror', sceneNow));

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1600);
    return () => clearTimeout(t);
  }, [flash]);

  const rebuild = useCallback(() => {
    const { centre, folds: f, flip: fl } = live.current;
    layerRef.current.clear();
    layerRef.current.paint((ctx) => {
      ctx.save();
      ctx.translate(centre.x, centre.y);
      for (const s of strokesRef.current) paintMirrored(ctx, s, f, fl);
      ctx.restore();
    });
  }, []);

  // Changing folds or flip re-forms every stroke already on the paper.
  useEffect(rebuild, [folds, flip, rebuild]);

  // The pattern that was here last time comes back, unfinished or reopened.
  useEffect(() => {
    const keeper = draftRef.current;
    const restored = initial ?? loadDraft('mirror');
    if (restored && restored.strokes.length > 0) {
      strokesRef.current = restored.strokes;
      setCount(restored.strokes.length);
      if (restored.folds !== undefined) setFolds(restored.folds);
      if (restored.flip !== undefined) setFlip(restored.flip);
      setFlash(initial ? 'Keep drawing' : 'Your pattern came back');
      rebuild();
    }
    return () => keeper.stop();
  }, [initial, rebuild]);

  useSurface(canvasRef, {
    onResize: ({ width, height }) => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      layerRef.current.resize(width, height, dpr);
      live.current.centre = { x: width / 2, y: height / 2 };
      rebuild();
    },
    onFrame: (ctx, _dt, { width, height }) => {
      const { centre, folds: f, flip: fl } = live.current;
      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      // Faint fold guides so the symmetry is visible before you draw.
      ctx.save();
      ctx.strokeStyle = token('--paper-line');
      ctx.lineWidth = 2;
      ctx.translate(centre.x, centre.y);
      const r = Math.hypot(width, height) / 2;
      for (let i = 0; i < f; i++) {
        ctx.save();
        ctx.rotate((i / f) * Math.PI * 2);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(r, 0);
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();

      layerRef.current.blit(ctx);
      ctx.save();
      ctx.translate(centre.x, centre.y);
      for (const s of activeRef.current.values()) paintMirrored(ctx, s, f, fl);
      ctx.restore();
    },
    onDown: (p) => {
      const stroke = createStroke('marker', live.current.color, 12, Math.floor(Math.random() * 100000));
      extendStroke(stroke, local(p.pos));
      activeRef.current.set(p.id, stroke);
    },
    onMove: (p) => {
      const stroke = activeRef.current.get(p.id);
      if (stroke) extendStroke(stroke, local(p.pos));
    },
    onUp: (p) => {
      const stroke = activeRef.current.get(p.id);
      if (!stroke) return;
      activeRef.current.delete(p.id);
      const done = finishStroke(stroke);
      strokesRef.current.push(done);
      const { centre, folds: f, flip: fl } = live.current;
      layerRef.current.paint((ctx) => {
        ctx.save();
        ctx.translate(centre.x, centre.y);
        paintMirrored(ctx, done, f, fl);
        ctx.restore();
      });
      setCount(strokesRef.current.length);
      draftRef.current.touch();
      play('sparkle', 0.4);
    },
  });

  /** Screen point to a point measured from the centre of the fold. */
  function local(pos: { x: number; y: number }) {
    return { x: pos.x - live.current.centre.x, y: pos.y - live.current.centre.y };
  }

  const undo = () => {
    if (strokesRef.current.length === 0) return;
    strokesRef.current.pop();
    rebuild();
    setCount(strokesRef.current.length);
    draftRef.current.touch();
    play('pop', 0.4);
  };

  const clear = () => {
    strokesRef.current = [];
    activeRef.current.clear();
    layerRef.current.clear();
    setCount(0);
    setAskClear(false);
    draftRef.current.clear();
    play('whoosh', 0.6);
  };

  const save = () => {
    if (strokesRef.current.length === 0) return;
    draftRef.current.flush();
    play('yay', 0.7);
    setFlash('Kept it');
    void keepPicture(sceneNow()).then((result) => {
      if (result.ok) onSaved();
      else setFlash(result.message);
    });
  };

  const empty = count === 0;

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Mirror magic" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton round label="Undo" hideLabel disabled={empty} onClick={undo}>
              <Undo2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Start again" hideLabel disabled={empty} onClick={() => setAskClear(true)}>
              <Trash2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Keep this picture" hideLabel disabled={empty} onClick={save}>
              <Star size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>
        <p class="readout">
          <strong>{folds}</strong> mirrors
        </p>
        {flash && <Coach>{flash}</Coach>}
        {!flash && empty && <Coach>Draw one line</Coach>}
      </div>

      <div class="dock">
        {tabs}
        <CrayonRow value={color} onChange={setColor} />
        <div class="dock__row">
          <Stepper value={folds} min={2} max={12} step={1} label="mirrors" onChange={setFolds} />
          <div class="dock__spacer" />
          <Chip label="Flip" pressed={flip} onClick={() => setFlip((v) => !v)}>
            <FlipHorizontal2 size={20} strokeWidth={2.75} />
          </Chip>
        </div>
      </div>

      {askClear && (
        <ConfirmSheet title="Start a new pattern?" confirmLabel="Start again" onConfirm={clear} onCancel={() => setAskClear(false)} />
      )}
    </div>
  );
}
